import * as Sentry from '@sentry/react'
import { browserTracingIntegration } from '@sentry/react'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoPermissions } from 'features/permissions'
import snakeCase from 'lodash/snakeCase'
import mixpanel, { Dict } from 'mixpanel-browser'
import React, { useContext, useEffect } from 'react'

import { LOGIN } from './index'

type AnyValidPropertyValue =
  | string
  | number
  | boolean
  | object
  | undefined
  | null
  | Record<string, string | number | boolean | object | undefined | null>
type EventPropertiesType = Record<string, AnyValidPropertyValue>

type GeckoAnalyticsContextType = {
  trackButtonClicked: (
    properties: { button_name: string } & EventPropertiesType
  ) => void
  trackSliderChanged: (
    properties: { slider_name: string } & EventPropertiesType
  ) => void
  trackEvent: (eventName: string, eventProperties?: EventPropertiesType) => void
  combinedProps: Dict
}

const GeckoAnalyticsContext = React.createContext<
  GeckoAnalyticsContextType | undefined
>(undefined)

//------------------------------------------------------------------------------

/** A hook for accessing scoped analytics and reporting methods */
export const useGeckoAnalytics = (): GeckoAnalyticsContextType => {
  const context = useContext(GeckoAnalyticsContext)
  if (!context)
    throw new Error(
      'useGeckoAnalytics() must be used within GeckoAnalyticsProvider'
    )
  return context
}

//------------------------------------------------------------------------------

/**
 * A provider component offering scoped access to analytics/reporting. These
 * may be nested to describe a more granular view of the application's state
 * (for example, within specific extensions or sub-views)
 * @param children          Children of this pass-through component
 * @param props             Any data to attach to outgoing events
 * @constructor
 */
export const GeckoAnalyticsProvider: React.FC<
  { children: React.ReactNode } & Dict
> = ({ children, ...props }) => {
  const higherContext = React.useContext(GeckoAnalyticsContext)
  const { ENVIRONMENT, MIXPANEL_AUTH_TOKEN, SENTRY_DSN } = useRuntimeVariables()
  const { user: currentUser } = useGeckoPermissions()

  // The top-level context should initialize Mixpanel and Sentry
  useEffect(() => {
    if (higherContext) return
    if (!currentUser) return

    mixpanel.init(MIXPANEL_AUTH_TOKEN, {
      persistence: 'localStorage',
      track_pageview: 'url-with-path-and-query-string',
    })
    mixpanel.register({ app: 'Forecast' })
    mixpanel.identify(currentUser.email)
    mixpanel.track(LOGIN.title)

    if (import.meta.env.MODE === 'production' && SENTRY_DSN !== '') {
      if (!Sentry.isInitialized()) {
        Sentry.init({
          dsn: SENTRY_DSN,
          integrations: [
            browserTracingIntegration(),
            Sentry.replayIntegration({
              maskAllText: false,
              maskAllInputs: false,
              blockAllMedia: false,
            }),
            Sentry.replayCanvasIntegration(),
          ],
          replaysOnErrorSampleRate: 1.0,
          replaysSessionSampleRate: 1.0,
          sendDefaultPii: true,
          environment: ENVIRONMENT,
          tracesSampleRate: 0.25,
        })
      }
      Sentry.setUser({ email: currentUser.email })
    }
  }, [higherContext, currentUser, MIXPANEL_AUTH_TOKEN, SENTRY_DSN, ENVIRONMENT])

  const combinedProps = React.useMemo(
    () => forceSnakeCaseKeys({ ...props, ...higherContext?.combinedProps }),
    [higherContext, props]
  )

  const trackEvent = React.useCallback(
    (eventName: string, eventProperties?: Dict) => {
      mixpanel.track(
        eventName,
        !eventProperties
          ? combinedProps
          : { ...forceSnakeCaseKeys(eventProperties), ...combinedProps }
      )
    },
    [combinedProps]
  )

  const trackButtonClicked = React.useCallback(
    (properties: Dict) => trackEvent('Button Clicked', properties),
    [trackEvent]
  )

  const trackSliderChanged = React.useCallback(
    (properties: Dict) => trackEvent('Slider Changed', properties),
    [trackEvent]
  )

  const value = React.useMemo(
    () => ({
      trackButtonClicked,
      trackSliderChanged,
      trackEvent,
      combinedProps,
    }),
    [combinedProps, trackButtonClicked, trackSliderChanged, trackEvent]
  )

  return (
    <GeckoAnalyticsContext.Provider value={value}>
      {children}
    </GeckoAnalyticsContext.Provider>
  )
}

// The following code ensures that our event properties are standardized
// Shamelessly stolen from https://stackoverflow.com/questions/12931828
const forceSnakeCaseKeys = (obj: Dict): Dict =>
  obj !== null && obj !== undefined && obj.constructor === Object
    ? Object.keys(obj).reduce(
        (result, key) => ({
          ...result,
          [snakeCase(key)]: forceSnakeCaseKeys(obj[key]),
        }),
        {}
      )
    : obj
